import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    useTheme,
    Snackbar,
    Alert,
} from '@mui/material';
import ActionBar from '../components/ActionBar';
import AlertBar from '../components/AlertBar';
import PromptBar from '../components/PromptBar';
import LoadingSpinner from '../components/LoadingSpinner';
import AbstractBackground from '../components/AbstractBackground';
import StaticBackground from '../components/StaticBackground';
import actionBarConfig from '../config/actionBarConfig';
import promptBarConfig from '../config/promptBarConfig';
import EmptyStateTilesConfig from '../config/emptyStateTilesConfig';
import { sendMessage } from '../services/apiService';
import EmptyStateTiles from '../components/EmptyStateTiles';
import MessageRenderer from '../services/messageRenderer';
import PromptBarRibbon from '../components/PromptBarRibbon';
import Footer from '../components/Footer';
import {
    addMessage,
    clearMessages,
    selectChatContext,
} from '../reducers/messagesReducer';
import { setLoading } from '../reducers/loadingReducer';
import { showToast, hideToast } from '../reducers/uiReducer';
import {
    setShowTiles,
    setIsActiveChat,
    setSelectedTileId,
    clearSelectedTileId,
    addPromptItem,
    removePromptItem,
    updatePromptItem,
    clearPromptItems
} from '../reducers/uiReducer';
import ProfileAchievements from '../components/ProfileAchievements';
import ProfileUserInfo from '../components/ProfileUserInfo';
import useResponsive from '../hooks/useResponsive';

const Profile = () => {
    const dispatch = useDispatch();
    const theme = useTheme();

    const loading = useSelector((state) => state.loading);
    const toast = useSelector((state) => state.ui.toast);
    const user = useSelector((state) => state.auth.user);
    const containerRef = useRef(null);
    const { isTabletScreen, isMobileScreen } = useResponsive();

    const handleNewChat = () => {
        dispatch(clearSelectedTileId());
        dispatch(setShowTiles(true));
        dispatch(setIsActiveChat(false));
    };

    const memoizedBackground = useMemo(
        () => <AbstractBackground animate={loading} containerRef={containerRef} />, [loading]
    );



    return (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Box
                ref={containerRef}
                sx={{
                    height: '100vh',
                    width: '100%',
                    minWidth: '300px',
                    maxWidth: '1000px',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {memoizedBackground}

                <Box
                    sx={{
                        backgroundColor: theme.palette.background.default,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <ActionBar config={actionBarConfig} onShowTiles={handleNewChat} />
                </Box>

                <Box
                    sx={{
                        flexGrow: 1,
                        overflow: 'hidden',
                        display: 'flex',
                        position: 'relative',
                        overflowY: 'auto',
                        boxShadow: theme.shadows[1],
                        background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(to top,rgba(18, 18, 18, 0.8),rgba(29, 29, 29, 0.6))'
                            : 'linear-gradient(to top, rgba(255,255,255,0.2), rgba(0,116,211,0.1))',
                    }}
                >

                    <AlertBar
                        open={toast.open}
                        onClose={() => dispatch(hideToast())}
                        severity={toast.severity}
                        message={toast.message}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: isTabletScreen || isMobileScreen ? 'column' : 'row',
                            width: '100%',
                            padding: 2,
                            margin: 2,
                            borderRadius: 1,
                            backgroundColor: theme.palette.background.paper,
                            boxShadow: theme.shadows[2],
                        }}
                    >
                        <ProfileUserInfo />
                        <ProfileAchievements achievements={user.preferences.achievements} />
                    </Box>
                </Box>

                <Footer sx={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} />
            </Box>
        </Box>
    );
};

export default Profile;
